import { useRef, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getTotalUnreadCount } from '../api/messages';
import { useAuth } from '../contexts/AuthContext';
import { useCurrentUser } from './useCurrentUser';
import { EOrganizationPlan } from '../types/enum';

export const TOTAL_UNREAD_COUNT_QUERY_KEY = ['messages', 'total-unread-count'] as const;
const INVALIDATE_DEBOUNCE_MS = 200;

export function useDebouncedInvalidateTotalUnread() {
  const queryClient = useQueryClient();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  return useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      queryClient.invalidateQueries({ queryKey: TOTAL_UNREAD_COUNT_QUERY_KEY });
    }, INVALIDATE_DEBOUNCE_MS);
  }, [queryClient]);
}

export function useTotalUnreadCount() {
  const { user } = useAuth();
  const { data: currentUser } = useCurrentUser();
  // チャット機能が無いプランは集計APIが403を返す。バッジも表示しないため呼ばない。
  // プラン判明前に走らせると LIGHT が初回だけ403を踏むので、プロファイル取得を待つ
  const isLightPlan = currentUser?.organizationPlan === EOrganizationPlan.LIGHT;

  return useQuery({
    queryKey: TOTAL_UNREAD_COUNT_QUERY_KEY,
    queryFn: getTotalUnreadCount,
    enabled: !!user && !!currentUser && !isLightPlan,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });
}
